import type { BooruPost } from "@/lib/booru-api"

export interface OrderMediaItem {
  url: string
  thumbnailUrl?: string | null
  type?: string | null
  title?: string | null
}

export function inferOrderMediaType(url: string, fallback?: string | null) {
  const raw = (fallback || "").toLowerCase()
  const lowerUrl = url.toLowerCase()

  if (/\.(mp4|webm|mov|m4v|ogv|avi|mkv|flv|wmv|3gp)(\?|#|$)/i.test(lowerUrl)) return "video"
  if (/\.(gif|apng)(\?|#|$)/i.test(lowerUrl)) return "video"
  if (/\.(mp3|wav|ogg|m4a|flac)(\?|#|$)/i.test(url)) return "audio"
  if (/\.(jpe?g|png|webp|avif|bmp|svg)(\?|#|$)/i.test(lowerUrl)) return "image"
  if (/^https?:\/\/v\d*\.erome\.com\//i.test(url)) return "video"
  if (/^https?:\/\/[^/]*\/.*\/video\//i.test(url)) return "video"
  if (lowerUrl.includes("video-cdn.")) return "video"
  if (lowerUrl.includes("/videos/") || lowerUrl.includes("/video/")) return "video"
  if (/^https?:\/\/(?:i|s)\d*\.erome\.com\//i.test(url)) return "image"
  if (raw === "video" || raw === "audio" || raw === "image") return raw
  if (raw === "gif") return "video"
  return "image"
}

export function normalizeOrderMediaItems(value: unknown, maxItems = 250): OrderMediaItem[] {
  if (!Array.isArray(value)) return []

  const seen = new Set<string>()
  const items: OrderMediaItem[] = []
  for (const raw of value) {
    if (!raw || typeof raw !== "object") continue
    const record = raw as Record<string, unknown>
    const url = typeof record.url === "string" ? record.url.trim() : ""
    if (!url || seen.has(url)) continue
    seen.add(url)
    items.push({
      url,
      thumbnailUrl: typeof record.thumbnailUrl === "string" ? record.thumbnailUrl.trim() || null : null,
      type: typeof record.type === "string" ? inferOrderMediaType(url, record.type) : inferOrderMediaType(url),
      title: typeof record.title === "string" ? record.title.trim() || null : null,
    })
    if (items.length >= maxItems) break
  }

  return items
}

// Gallery sites (erome, doujins, xlecx) store only the gallery page URL on the order —
// individual media file URLs are resolved on demand via their gallery API. Used both by
// OrderContentViewer (preview) and by editor launchers (to build real multi-page content).
export async function resolveOrderMediaItems(opts: {
  url?: string | null
  site?: string | null
  type?: string | null
}): Promise<OrderMediaItem[]> {
  const { url, site, type } = opts
  if (!url) return []

  const lowerSite = (site || "").toLowerCase()
  const lowerUrl = url.toLowerCase()
  const shouldFetchDoujins = lowerSite === "doujins" || lowerUrl.includes("doujins.com")
  const shouldFetchXlecx = lowerSite.includes("xlecx") || lowerUrl.includes("xlecx.")
  const shouldFetchErome = lowerSite === "erome" || lowerUrl.includes("erome.com/a/")
  if (!shouldFetchDoujins && !shouldFetchXlecx && !shouldFetchErome) return []

  const endpoint = shouldFetchDoujins
    ? "/api/booru/doujins/gallery"
    : shouldFetchXlecx
      ? "/api/booru/xlecx/gallery"
      : "/api/booru/erome/gallery"

  try {
    const res = await fetch(`${endpoint}?url=${encodeURIComponent(url)}`)
    if (!res.ok) return []
    const data = await res.json()
    const pages = (data.pages || [])
      .map((page: any) => ({
        url: page.imageUrl || page.url,
        thumbnailUrl: page.thumbnailUrl || page.previewUrl || null,
        type: page.type || (page.isVideo ? "video" : page.audioUrl ? "audio" : inferOrderMediaType(page.imageUrl || page.url || "", type)),
      }))
      .filter((page: OrderMediaItem) => page.url)
    return normalizeOrderMediaItems(pages)
  } catch {
    return []
  }
}

export function mediaItemsFromBooruPost(post: BooruPost): OrderMediaItem[] {
  const albumImages = Array.isArray(post.albumImages) ? post.albumImages.filter(Boolean) : []
  if (albumImages.length > 0) {
    return normalizeOrderMediaItems(albumImages.map((url, index) => ({
      url,
      thumbnailUrl: index === 0 ? post.previewUrl || post.sampleUrl || null : null,
      type: inferOrderMediaType(url, post.type),
      title: post.title ? `${post.title} ${index + 1}` : `Page ${index + 1}`,
    })))
  }

  // post.source/post.postUrl are often the artist's original post page (e.g. a twitter/x.com
  // status link), not a direct media file — falling back to them here silently produces an
  // unloadable "image" (the page 403s instead of serving an image). Only use them if they
  // actually look like a direct file.
  const sourceIsMedia = !!post.source && /\.(jpe?g|png|gif|webp|avif|bmp|mp4|webm|mov|m4v)(\?.*)?$/i.test(post.source)
  const postUrlIsMedia = !!post.postUrl && /\.(jpe?g|png|gif|webp|avif|bmp|mp4|webm|mov|m4v)(\?.*)?$/i.test(post.postUrl)
  const mediaUrl = post.fileUrl || post.sampleUrl || (sourceIsMedia ? post.source : "") || (postUrlIsMedia ? post.postUrl : "") || ""
  if (!mediaUrl) return []
  return normalizeOrderMediaItems([{
    url: mediaUrl,
    thumbnailUrl: post.previewUrl || post.sampleUrl || null,
    type: inferOrderMediaType(mediaUrl, post.type),
    title: post.title || post.albumTitle || null,
  }])
}
